/**
 * Trust + citation helpers for the Etch chat surface (design spec §9.1, §11.2).
 *
 * The backend emits a `chat:sources` event with descriptors of the shape
 *
 *   { id:'s1', kind:'code'|'document'|'catalog'|'ckb', title, ref, locator,
 *     snippet, score, authority? }
 *
 * and inline markers `[S1]`, `[S2]` … inside the answer text (marker =
 * `id.toUpperCase()`). Everything in this module derives from those two inputs
 * only — it never invents a citation the model did not make, and it never
 * silently drops a marker whose source is missing (spec §11.2: a source that
 * cannot be reached must stay visible as "nicht erreichbar").
 */

/** Matches an inline citation marker. Case-insensitive per the wire contract. */
export const CITATION_MARKER_RE = /\[(s\d+)\]/gi;

/** Trust states in the order the UI escalates them. */
export const TRUST_STATES = ['grounded', 'inference', 'model'];

/** i18n keys for each trust state — labels are never colour-only (spec §12). */
export const TRUST_LABEL_KEY = {
  grounded: 'chat.trustGrounded',
  inference: 'chat.trustInference',
  model: 'chat.trustModel',
};

/**
 * Every marker in `text`, lower-cased and de-duplicated, in first-appearance
 * order.
 * @param {string} text
 * @returns {string[]} e.g. ['s1','s3']
 */
export function extractMarkers(text = '') {
  if (typeof text !== 'string' || !text) return [];
  const seen = [];
  const re = new RegExp(CITATION_MARKER_RE.source, 'gi');
  let match;
  while ((match = re.exec(text)) !== null) {
    const id = match[1].toLowerCase();
    if (!seen.includes(id)) seen.push(id);
  }
  return seen;
}

/**
 * The number shown inside the citation chip. Derived from the id so the chip
 * always matches the marker the model wrote ([S3] → 3), never from array order.
 * @param {string} id
 * @returns {number|null}
 */
export function citationIndex(id) {
  const match = /(\d+)/.exec(String(id || ''));
  return match ? Number(match[1]) : null;
}

/**
 * Reachability of a single source descriptor.
 * `state` is honoured when the backend starts sending one; until then it is
 * inferred, because a descriptor without any locator or snippet is a source
 * the retrieval layer could not actually open.
 * @returns {'ok'|'stale'|'unreachable'}
 */
export function sourceState(source) {
  if (!source) return 'unreachable';
  if (source.state === 'ok' || source.state === 'stale' || source.state === 'unreachable') {
    return source.state;
  }
  if (source.missing || source.unreachable || source.error) return 'unreachable';
  if (source.stale) return 'stale';
  if (!source.ref && !source.snippet) return 'unreachable';
  return 'ok';
}

/**
 * Merges the answer text with the source descriptors into the list the
 * SourcePanel renders.
 *
 * - every descriptor is kept, cited or not (`cited` says which),
 * - every marker without a descriptor becomes a synthetic `unreachable` entry
 *   so the reader sees that a claim points at something we could not fetch.
 *
 * @param {{content?: string, sources?: object[]}} input
 * @returns {Array<object & {index: number|null, state: string, cited: boolean}>}
 */
export function resolveSources({ content = '', sources = [] } = {}) {
  const list = Array.isArray(sources) ? sources : [];
  const markers = extractMarkers(content);
  const byId = new Map();

  for (const source of list) {
    if (!source) continue;
    const id = String(source.id ?? '').toLowerCase();
    byId.set(id, {
      ...source,
      id,
      index: citationIndex(id),
      state: sourceState(source),
      cited: markers.includes(id),
    });
  }

  for (const id of markers) {
    if (byId.has(id)) continue;
    byId.set(id, {
      id,
      kind: 'unknown',
      title: null,
      ref: null,
      locator: null,
      snippet: null,
      index: citationIndex(id),
      state: 'unreachable',
      cited: true,
      missing: true,
    });
  }

  return [...byId.values()].sort((a, b) => {
    if (a.index != null && b.index != null) return a.index - b.index;
    if (a.index != null) return -1;
    if (b.index != null) return 1;
    return 0;
  });
}

/**
 * The message-level trust state (spec §11.2).
 *
 * grounded  — the answer cites company sources inline,
 * inference — sources were retrieved but the model drew its own conclusion,
 * model     — nothing from the company corpus backs this answer.
 *
 * @param {{content?: string, sources?: object[], trust?: {grounded?: boolean, markers?: string[]}}} message
 * @returns {'grounded'|'inference'|'model'}
 */
export function resolveTrust({ content = '', sources = [], trust = null } = {}) {
  if (extractMarkers(content).length > 0) return 'grounded';
  if (Array.isArray(trust?.markers) && trust.markers.length > 0) return 'grounded';
  if (trust?.grounded === true) return 'grounded';
  if (Array.isArray(sources) && sources.length > 0) return 'inference';
  return 'model';
}

/**
 * True when at least one source of the answer could not be reached — surfaced
 * next to the trust badge so a partially broken retrieval is never invisible.
 */
export function hasUnreachableSource(resolved = []) {
  return resolved.some((source) => source.state === 'unreachable' || source.state === 'stale');
}

/**
 * Human-readable reference for the "copy reference" action.
 * Deliberately plain text: it is pasted into tickets and chat messages.
 */
export function formatSourceReference(source) {
  if (!source) return '';
  const parts = [source.title, source.ref, source.locator].filter(Boolean);
  return parts.join(' — ');
}
